import { useState } from 'react';
import { GOLD, MUTED, BORDER, GREEN, RED } from '../constants';
import { ITEM_CATEGORIES, METAL_TYPES, CHANNELS, DIRECTIONS, GRADE_SVCS } from '../constants';
import { today, uid, fmt, fmtDec } from '../utils';
import {
  FormField, Input, Select, PrimaryBtn, GhostBtn, DangerBtn,
  SectionTitle, TransactionTable, Card,
} from './UI';

const defaultForm = () => ({
  date: today(), direction: 'Purchase', channel: 'Retail',
  vendor: '', notes: '', invoiceNumber: '',
});
const defaultItem = () => ({
  id: uid(), category: ITEM_CATEGORIES[0], description: '',
  metal: 'Gold', grade: 'Raw', gradeService: 'Raw',
  qty: 1, unitPrice: '', weight: '',
});

export default function TransactionsTab({ transactions, onAdd, onDelete }) {
  const [form,     setForm]     = useState(defaultForm());
  const [items,    setItems]    = useState([defaultItem()]);
  const [showForm, setShowForm] = useState(false);
  const [filter,   setFilter]   = useState({ direction: 'all', channel: 'all', metal: 'all', dateFrom: '', dateTo: '', search: '' });
  const [page,     setPage]     = useState(0);
  const PAGE_SIZE = 50;

  function setF(k, v) { setForm((p) => ({ ...p, [k]: v })); }
  function updateItem(id, k, v) { setItems((p) => p.map((i) => i.id === id ? { ...i, [k]: v } : i)); }

  function handleSubmit(e) {
    e.preventDefault();
    let added = 0;
    items.forEach((item) => {
      if (!item.unitPrice) return;
      const qty   = parseFloat(item.qty)       || 1;
      const price = parseFloat(item.unitPrice) || 0;
      onAdd({
        ...form,
        category:     item.category,
        description:  item.description,
        metal:        item.metal,
        grade:        item.grade,
        gradeService: item.gradeService,
        qty,
        unitPrice:    price,
        totalAmount:  qty * price,
        weight:       parseFloat(item.weight) || 0,
        source:       'manual',
      });
      added++;
    });
    if (added > 0) {
      setForm(defaultForm());
      setItems([defaultItem()]);
      setShowForm(false);
    }
  }

  const filtered = transactions.filter((t) => {
    if (filter.direction !== 'all' && t.direction !== filter.direction) return false;
    if (filter.channel   !== 'all' && t.channel   !== filter.channel)   return false;
    if (filter.metal     !== 'all' && t.metal     !== filter.metal)     return false;
    if (filter.dateFrom  && t.date < filter.dateFrom) return false;
    if (filter.dateTo    && t.date > filter.dateTo)   return false;
    if (filter.search) {
      const s = filter.search.toLowerCase();
      if (!`${t.description} ${t.vendor} ${t.category} ${t.notes}`.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows   = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Totals for filtered set
  const filteredSales = filtered.filter(t=>t.direction==='Sale').reduce((s,t)=>s+t.totalAmount,0);
  const filteredBuys  = filtered.filter(t=>t.direction==='Purchase').reduce((s,t)=>s+t.totalAmount,0);

  return (
    <div>
      <SectionTitle
        title="Transactions"
        sub={`${filtered.length} records · Sales ${fmt(filteredSales)} · Buys ${fmt(filteredBuys)}`}
        action={<PrimaryBtn onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : '+ New Transaction'}</PrimaryBtn>}
      />

      {/* FORM */}
      {showForm && (
        <Card style={{ padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: GOLD, marginBottom: 18, letterSpacing: 1 }}>NEW TRANSACTION</div>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              <FormField label="Date" minWidth={130}>
                <Input type="date" value={form.date} onChange={e=>setF('date',e.target.value)} />
              </FormField>
              <FormField label="Direction" minWidth={110}>
                <Select value={form.direction} onChange={e=>setF('direction',e.target.value)}>
                  {DIRECTIONS.map(d=><option key={d}>{d}</option>)}
                </Select>
              </FormField>
              <FormField label="Channel" minWidth={110}>
                <Select value={form.channel} onChange={e=>setF('channel',e.target.value)}>
                  {CHANNELS.map(c=><option key={c}>{c}</option>)}
                </Select>
              </FormField>
              <FormField label="Vendor / Customer" flex={2}>
                <Input value={form.vendor} onChange={e=>setF('vendor',e.target.value)} placeholder="Name" />
              </FormField>
              <FormField label="Invoice Ref" minWidth={120}>
                <Input value={form.invoiceNumber} onChange={e=>setF('invoiceNumber',e.target.value)} placeholder="Optional" />
              </FormField>
            </div>

            {/* LINE ITEMS */}
            <div style={{ fontSize: 10, color: MUTED, letterSpacing: 1.5, marginBottom: 10 }}>LINE ITEMS</div>
            {items.map((item) => (
              <div key={item.id} style={{ background: '#0D1117', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '14px 16px', marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <FormField label="Category" minWidth={150} flex={2}>
                    <Select value={item.category} onChange={e=>updateItem(item.id,'category',e.target.value)} style={{ fontSize: 11 }}>
                      {ITEM_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Description" flex={3} minWidth={180}>
                    <Input value={item.description} onChange={e=>updateItem(item.id,'description',e.target.value)} placeholder="e.g. 1oz AGE MS65, Lot of 20x Morgans" style={{ fontSize: 11 }} />
                  </FormField>
                  <FormField label="Metal" minWidth={100}>
                    <Select value={item.metal} onChange={e=>updateItem(item.id,'metal',e.target.value)} style={{ fontSize: 11 }}>
                      {METAL_TYPES.map(m=><option key={m}>{m}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Grade Svc" minWidth={90}>
                    <Select value={item.gradeService} onChange={e=>updateItem(item.id,'gradeService',e.target.value)} style={{ fontSize: 11 }}>
                      {GRADE_SVCS.map(g=><option key={g}>{g}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Grade" minWidth={70}>
                    <Input value={item.grade} onChange={e=>updateItem(item.id,'grade',e.target.value)} placeholder="MS65" style={{ fontSize: 11 }} />
                  </FormField>
                  <FormField label="Qty" minWidth={60} flex={0}>
                    <Input type="number" min="0.001" step="any" value={item.qty} onChange={e=>updateItem(item.id,'qty',e.target.value)} style={{ fontSize: 11, width: 64 }} />
                  </FormField>
                  <FormField label="Unit Price $" minWidth={100} flex={0}>
                    <Input type="number" step="0.01" value={item.unitPrice} onChange={e=>updateItem(item.id,'unitPrice',e.target.value)} placeholder="0.00" style={{ fontSize: 11, width: 100 }} />
                  </FormField>
                  <FormField label="Weight (oz)" minWidth={90} flex={0}>
                    <Input type="number" step="0.0001" value={item.weight} onChange={e=>updateItem(item.id,'weight',e.target.value)} placeholder="0" style={{ fontSize: 11, width: 90 }} />
                  </FormField>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 2 }}>
                    <span style={{ color: GOLD, fontSize: 14, fontWeight: 500, minWidth: 80 }}>
                      {item.qty && item.unitPrice ? fmt((parseFloat(item.qty)||1)*(parseFloat(item.unitPrice)||0)) : '—'}
                    </span>
                    {items.length > 1 && (
                      <DangerBtn onClick={()=>setItems(p=>p.filter(i=>i.id!==item.id))}>✕</DangerBtn>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <GhostBtn type="button" onClick={()=>setItems(p=>[...p,defaultItem()])}>+ Add Line Item</GhostBtn>
              <span style={{ color: GOLD, fontSize: 15 }}>
                Total: {fmt(items.reduce((s,i)=>s+(parseFloat(i.qty)||1)*(parseFloat(i.unitPrice)||0),0))}
              </span>
            </div>

            <FormField label="Notes">
              <Input value={form.notes} onChange={e=>setF('notes',e.target.value)} placeholder="Optional notes" />
            </FormField>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <PrimaryBtn type="submit">Save Transaction</PrimaryBtn>
              <GhostBtn type="button" onClick={()=>setShowForm(false)}>Cancel</GhostBtn>
            </div>
          </form>
        </Card>
      )}

      {/* FILTERS */}
      <Card style={{ padding: '14px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FormField label="Search" flex={2} minWidth={160}>
            <Input value={filter.search} onChange={e=>setFilter({...filter,search:e.target.value})} placeholder="Description, vendor, notes…" />
          </FormField>
          <FormField label="Direction" minWidth={100}>
            <Select value={filter.direction} onChange={e=>setFilter({...filter,direction:e.target.value})}>
              <option value="all">All</option>
              {DIRECTIONS.map(d=><option key={d}>{d}</option>)}
            </Select>
          </FormField>
          <FormField label="Channel" minWidth={100}>
            <Select value={filter.channel} onChange={e=>setFilter({...filter,channel:e.target.value})}>
              <option value="all">All</option>
              {CHANNELS.map(c=><option key={c}>{c}</option>)}
            </Select>
          </FormField>
          <FormField label="Metal" minWidth={100}>
            <Select value={filter.metal} onChange={e=>setFilter({...filter,metal:e.target.value})}>
              <option value="all">All Metals</option>
              {METAL_TYPES.map(m=><option key={m}>{m}</option>)}
            </Select>
          </FormField>
          <FormField label="From" minWidth={130}>
            <Input type="date" value={filter.dateFrom} onChange={e=>setFilter({...filter,dateFrom:e.target.value})} />
          </FormField>
          <FormField label="To" minWidth={130}>
            <Input type="date" value={filter.dateTo} onChange={e=>setFilter({...filter,dateTo:e.target.value})} />
          </FormField>
          <GhostBtn onClick={()=>setFilter({ direction:'all', channel:'all', metal:'all', dateFrom:'', dateTo:'', search:'' })}>Clear</GhostBtn>
        </div>
      </Card>

      <TransactionTable rows={pageRows} onDelete={onDelete} />

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <GhostBtn onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0}>← Prev</GhostBtn>
          <span style={{ color: MUTED, fontSize: 11, alignSelf: 'center' }}>Page {page+1} of {totalPages}</span>
          <GhostBtn onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1}>Next →</GhostBtn>
        </div>
      )}
    </div>
  );
}
